'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { List, X } from '@phosphor-icons/react';

import { Box, Button, AppBar, Toolbar, Stack, Typography, Divider } from '@unity/cloud-ui';
import { UnityColor } from '@unity/cloud-ui/icons/brands';
import { styled, useTheme } from '@unity/cloud-ui/styles';
import useMediaQuery from '@unity/cloud-ui/useMediaQuery';

import { ThemeSwitcher, ZoomSwitcher } from '../theme';
import { LocaleSwitcher } from './components/locale-switcher';
import { QuickAccessMenu } from './components/quick-access-menu';
import { Preview } from './components/preview';

import { HeaderMenuLinks } from './components/header-menu-links';
import { HeaderSearchInput } from './components/header-search-input';

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  paddingLeft: 0,
  padding: theme.spacing(0.75, 1.5, 0.75, 1),
  justifyContent: 'space-between',
  backgroundColor: theme.vars.palette.background.paper,
  boxShadow: 'none',
  borderBottom: `1px solid ${theme.vars.palette.divider}`,
  minHeight: theme.layout.headerHeight,
  height: theme.layout.headerHeight,
}));

export const Header = () => {
  const t = useTranslations();
  const theme = useTheme();
  const linksContainer = React.useRef(null);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [isMenuOpen, setMenuOpen] = useState(false);

  React.useEffect(() => {
    if (isDesktop) {
      // force the mobile menu to close when moving from
      // mobile to desktop so the HeaderMenuLinksMobile variant
      // can clean the body overflow hidden
      setMenuOpen(false);
    }
  }, [isDesktop, setMenuOpen]);

  const handleMenuClick = () => {
    setMenuOpen(!isMenuOpen);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
  };

  return (
    <AppBar sx={{ boxShadow: 'none', position: { xs: 'static', md: 'fixed' } }} color="transparent" component="div">
      <StyledToolbar disableGutters variant="dense">
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Box sx={{ display: { xs: 'block', sm: 'block', md: 'none' } }}>
            <Button
              data-testid="mobile-menu-toggler"
              aria-label={t('header:menu:aria-label')}
              color="default"
              onClick={handleMenuClick}
            >
              {isMenuOpen ? <X size={24} /> : <List size={24} />}
            </Button>
          </Box>
          <Button color="default" component={Link} href="/">
            <Stack direction="row" alignItems="center" gap={0.5}>
              <UnityColor sx={{ fontSize: '1.25rem', marginRight: (theme) => theme.spacing(0.5) }} />
              {/* eslint-disable-next-line react/jsx-no-literals */}
              <Typography variant="h2" sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}>
                Documentation
              </Typography>
            </Stack>
          </Button>
          <Box ref={linksContainer} sx={{ display: { xs: 'none', sm: 'none', md: 'flex' } }}>
            <Divider orientation="vertical" variant="middle" flexItem sx={{ ml: '3px', mr: '6px' }} />
            <HeaderMenuLinks variant="desktop" container={linksContainer.current} />
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <HeaderSearchInput onOpenMobileSearchClick={handleMenuClose} />
          <Preview />
          <ZoomSwitcher />
          <ThemeSwitcher />
          <LocaleSwitcher />
          <QuickAccessMenu />
        </Stack>
      </StyledToolbar>
      <Box sx={{ display: { xs: 'block', sm: 'block', md: 'none' } }}>
        <HeaderMenuLinks variant="mobile" open={isMenuOpen} />
      </Box>
    </AppBar>
  );
};

export default Header;
